import React, { useState } from 'react';
import { db } from '../../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './ExistingBusiness.css';

const ExistingBusiness = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    fullName: '',
    email: '',
    phone: '',
    updateType: '',
    newDetails: '',
    updateReason: ''
  });
  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStatusOverlay, setShowStatusOverlay] = useState(false);

  const updateOptions = [
    'Contact Information',
    'Business Hours',
    'Address',
    'Location Area',
    'Business Category',
    'Business Description'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('');
    setIsSubmitting(true);

    try {
      if (!formData.businessName.trim()) {
        throw new Error('Business name is required');
      }
      if (!formData.updateType) {
        throw new Error('Please select what you would like to update');
      }
      if (!formData.newDetails.trim()) {
        throw new Error('New details are required');
      }
      if (!formData.updateReason.trim()) {
        throw new Error('Please provide a reason for the update');
      }

      const submissionData = {
        businessName: formData.businessName.trim(),
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        updateType: formData.updateType,
        newDetails: formData.newDetails.trim(),
        updateReason: formData.updateReason.trim(),
        timestamp: serverTimestamp(),
        type: 'update',
        status: 'pending'
      };

      const docRef = await addDoc(collection(db, 'businessRequests'), submissionData);

      if (!docRef.id) {
        throw new Error('Failed to submit form');
      }

      setSubmitStatus('Successfully submitted! Your update request is being reviewed.');
      setShowStatusOverlay(true);
      
      setFormData({
        businessName: '',
        fullName: '',
        email: '',
        phone: '',
        updateType: '',
        newDetails: '',
        updateReason: ''
      });

    } catch (error) {
      console.error('Submission Error:', error);
      
      if (error.code === 'permission-denied') {
        setSubmitStatus('Unable to submit form. Please check all required fields and try again.');
      } else if (error.code === 'unavailable') {
        setSubmitStatus('Network error. Please check your connection and try again.');
      } else {
        setSubmitStatus(error.message || 'Error submitting form. Please try again.');
      }
      setShowStatusOverlay(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const StatusMessage = ({ message, type }) => (
    <div 
      className={`submit-status ${type}`}
    >
      <i className={`fas ${type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
      <span>{message}</span>
    </div>
  );

  const StatusOverlay = ({ message, type, onClose }) => {
    return (
      <div 
        className="mobile-status-overlay"
        onClick={() => setShowStatusOverlay(false)}
      >
        <div 
          className="mobile-status-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`status-icon ${type}`}>
            {type === 'success' ? (
              <i className="fas fa-check-circle"></i>
            ) : (
              <i className="fas fa-exclamation-circle"></i>
            )}
          </div>
          <p>{message}</p>
          <small>Tap anywhere to close</small>
        </div>
      </div>
    );
  };

  return (
    <div className="existing-business-section">
      <div className="content-wrapper">
        <h1>Keep Your Business Details Current</h1>
        <p className="subtitle">Outdated information could be costing you customers</p>

        <div className="benefits-grid">
          <div className="benefit-card">
            <div className="icon">
              <i className="fas fa-search fa-3x"></i>
            </div>
            <h3>Be Found Easily</h3>
            <p>Ensure customers can find your correct location and contact details</p>
          </div>

          <div className="benefit-card">
            <div className="icon">
              <i className="fas fa-clock fa-3x"></i>
            </div>
            <h3>Never Miss a Customer</h3>
            <p>Display accurate business hours and avoid disappointed customers</p>
          </div>

          <div className="benefit-card">
            <div className="icon">
              <i className="fas fa-chart-line fa-3x"></i>
            </div>
            <h3>Maximize Revenue</h3>
            <p>Up-to-date information means more potential clients can reach you</p>
          </div>
        </div>

        <div className="info-box">
          <div className="info-icon">
            <i className="fas fa-info-circle"></i>
          </div>
          <div className="info-content">
            <h4>Did You Know?</h4>
            <p>Businesses with accurate, up-to-date information receive up to 50% more customer inquiries. Don't miss out on potential revenue - keep your details current!</p>
          </div>
        </div>

        <div className="update-form-container">
          <h2>Update Your Business Information</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Business Name *</label>
              <input
                type="text"
                name="businessName"
                value={formData.businessName || ''}
                onChange={handleInputChange}
                required
                placeholder="Enter your registered business name"
              />
            </div>

            <div className="form-group">
              <label>Name & Surname *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName || ''}
                onChange={handleInputChange}
                required
                placeholder="Enter your full name"
              />
            </div>

            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleInputChange}
                required
                placeholder="Enter your email address"
              />
            </div>

            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone || ''}
                onChange={handleInputChange}
                required
                placeholder="Enter your phone number"
              />
            </div>

            <div className="form-group">
              <label>What would you like to update? *</label>
              <select
                name="updateType"
                value={formData.updateType || ''}
                onChange={handleInputChange}
                required
              >
                <option value="">Select update type</option>
                {updateOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>New Details *</label>
              <textarea
                name="newDetails"
                value={formData.newDetails || ''}
                onChange={handleInputChange}
                required
                placeholder="Enter the new or updated information"
              />
            </div>

            <div className="form-group">
              <label>Reason for Update *</label>
              <textarea
                name="updateReason"
                value={formData.updateReason || ''}
                onChange={handleInputChange}
                required
                placeholder="Please explain why this update is needed"
              />
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Update Request'}
            </button>

            {submitStatus && (
              <StatusMessage 
                message={submitStatus} 
                type={submitStatus.includes('Successfully') ? 'success' : 'error'} 
              />
            )}
          </form>
        </div>
      </div>

      {showStatusOverlay && (
        <StatusOverlay 
          message={submitStatus}
          type={submitStatus.includes('Error') ? 'error' : 'success'}
          onClose={() => setShowStatusOverlay(false)}
        />
      )}
    </div>
  );
};

export default ExistingBusiness; 